import { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { Game, Region } from '../../interfaces/GameInterface';
import { imageUrl, FALLBACK_IMAGE } from '../../config';
import './CardsList.css';

const PAGE_SIZE = 24;

interface Props {
  games: Game[];
  searchTerm: string;
  region: Region | 'all';
}

// Split "Simulation , General" into clean tags.
function genreTags(genre: string): string[] {
  return genre
    .split(/[,/]/)
    .map((g) => g.trim())
    .filter(Boolean);
}

function year(date: string): string {
  return date ? date.slice(0, 4) : '';
}

function releasedInRegion(game: Game, region: Region | 'all'): boolean {
  if (region === 'all') return true;
  if (region === 'na') return !!game.usa_released;
  if (region === 'eu') return !!game.eu_released;
  return !!game.jp_released;
}

function RegionPills({ game }: { game: Game }) {
  const regions: { key: Region; label: string; date: string }[] = [
    { key: 'na', label: 'NA', date: game.usa_released },
    { key: 'eu', label: 'EU', date: game.eu_released },
    { key: 'jp', label: 'JP', date: game.jp_released },
  ];
  return (
    <div className="regions">
      {regions.map((r) => (
        <span
          key={r.key}
          className={`region-pill ${r.date ? 'has' : 'missing'}`}
          title={r.date ? `${r.label}: ${r.date}` : `${r.label}: unreleased`}
        >
          {r.label}
          {r.date && <em>{year(r.date)}</em>}
        </span>
      ))}
    </div>
  );
}

function GameCard({ game }: { game: Game }) {
  const tags = genreTags(game.genre);
  return (
    <article className="card">
      <div className="card-cover">
        <img
          src={imageUrl(game.image)}
          alt={`${game.title} cover art`}
          loading="lazy"
          onError={(e) => {
            const img = e.currentTarget;
            img.onerror = null;
            img.src = FALLBACK_IMAGE;
          }}
        />
        <RegionPills game={game} />
      </div>
      <div className="card-body">
        <h3 className="card-title" title={game.title}>
          {game.title}
        </h3>
        {tags.length > 0 && (
          <div className="tags">
            {tags.slice(0, 3).map((t) => (
              <span className="tag" key={t}>
                {t}
              </span>
            ))}
          </div>
        )}
        <dl className="meta">
          {game.developer && (
            <div>
              <dt>Developer</dt>
              <dd>{game.developer}</dd>
            </div>
          )}
          {game.publisher && (
            <div>
              <dt>Publisher</dt>
              <dd>{game.publisher}</dd>
            </div>
          )}
        </dl>
      </div>
    </article>
  );
}

function CardsList({ games, searchTerm, region }: Props) {
  const [visible, setVisible] = useState(PAGE_SIZE);

  const term = searchTerm.trim().toLowerCase();

  const filtered = useMemo(() => {
    return games.filter((game) => {
      if (!releasedInRegion(game, region)) return false;
      if (!term) return true;
      // Plain substring match — no RegExp, so special chars never crash.
      return (
        game.title.toLowerCase().includes(term) ||
        game.developer.toLowerCase().includes(term) ||
        game.publisher.toLowerCase().includes(term)
      );
    });
  }, [games, term, region]);

  // Reset the window whenever the result set changes.
  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [term, region]);

  const slice = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  if (filtered.length === 0) {
    return (
      <div className="state">
        <p className="state-title">No games found</p>
        <p className="state-sub">Try a different search or region.</p>
      </div>
    );
  }

  return (
    <>
      <p className="result-count">
        {filtered.length.toLocaleString()} game
        {filtered.length === 1 ? '' : 's'}
      </p>
      <InfiniteScroll
        pageStart={0}
        loadMore={() => setVisible((v) => v + PAGE_SIZE)}
        hasMore={hasMore}
        loader={<div className="loader" key="loader" />}
      >
        <div className="grid">
          {slice.map((game) => (
            <GameCard key={game.media_id || game.image} game={game} />
          ))}
        </div>
      </InfiniteScroll>
    </>
  );
}

export default CardsList;
